import Grid from "@components/Grid/Grid";
import { useEffect, useState } from "react";
import IconArrowLong from "../../public/image/svg/arrow-long.svg";
import styles from "./C249_FacultyProfileCarousel.module.scss";

import FacultyProfileMulti, {
  FacultyProfileMultiProps,
} from "./C249_FacultyProfileMulti";
import FacultyProfileSolo from "./C249_FacultyProfileSolo";

import { MQ_AND_BELOW, MQ_AND_UP } from "@customTypes/Breakpoints";
import clsx from "clsx";

import { TypeFacultyProfileCarouselFields } from "@customTypes/contentful";
import { A11y, Keyboard, Pagination } from "swiper/modules";
import { Swiper as SwiperCarousel, SwiperSlide } from "swiper/react";
import "swiper/scss";
import "swiper/scss/a11y";
import "swiper/scss/pagination";
import { Swiper as SwiperProps } from "swiper/types";

export const FacultyProfileCarousel = (props: {
  content: TypeFacultyProfileCarouselFields;
}) => {
  const { content } = props;
  const { facultyProfiles } = content;

  const [swiper, setSwiper] = useState<SwiperProps>();
  const [, setUpdate] = useState(0);

  const goToNextSlide = () => {
    swiper?.slideNext();
  };

  // isDesktop is used for when you have a grid of 3 items
  const [isDesktop, setIsDesktop] = useState(true);

  // isMobile to identify if we show carousel arrow
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsDesktop(window.innerWidth >= MQ_AND_UP.DESKTOP);
    setIsMobile(window.innerWidth <= MQ_AND_BELOW.MOBILE);

    window.addEventListener("resize", () => {
      setIsDesktop(window.innerWidth >= MQ_AND_UP.DESKTOP);
      setIsMobile(window.innerWidth <= MQ_AND_BELOW.MOBILE);
    });
  }, []);

  let swiperParams = {};

  const sharedParams = {
    pagination: { clickable: true },
    modules: [Pagination, Keyboard, A11y],
    slidesPerView: 1,
    slidesPerGroup: 1,
    loopAddBlankSlides: facultyProfiles.length < 8,
    spaceBetween: 20,
    className: styles.swiper,
    keyboard: { enabled: true, onlyInViewport: true },
    a11y: { enabled: true },

    // loop only when we have enough items and full groups of 4
    loop: facultyProfiles.length >= 8 && facultyProfiles.length % 4 === 0,
    // fill last group only when loop is active
    loopFillGroupWithBlank:
      facultyProfiles.length >= 8 && facultyProfiles.length % 4 === 0,
    // for non-full groups (e.g., 9 items → 4,4,1) use rewind instead of loop
    rewind: !(facultyProfiles.length >= 8 && facultyProfiles.length % 4 === 0),

    onSwiper: (swiper) => {
      setSwiper(swiper);
    },
    onSlideChange: () => {
      setUpdate((e) => e + 1);
    },
  };

  if (facultyProfiles.length < 5 && facultyProfiles.length !== 1) {
    swiperParams = sharedParams;
  } else if (facultyProfiles.length > 4) {
    swiperParams = {
      ...sharedParams,
      breakpoints: {
        1024: {
          slidesPerView: 4,
          slidesPerGroup: 4,
          spaceBetween: 40,
        },
        768: {
          slidesPerView: 2,
          slidesPerGroup: 2,
          spaceBetween: 40,
        },
      },
    };
  }

  const currentPage = Math.round((swiper?.snapIndex || 0) + 1);
  const slidesPerView = swiper?.slidesPerViewDynamic?.() || 1;
  const pagesCount = Math.ceil(
    (facultyProfiles?.length || 1) / Math.max(slidesPerView, 1)
  );
  const nextPage = currentPage >= pagesCount ? 1 : currentPage + 1;

  return (
    <div
      className={clsx(
        "component",
        styles["faculty-profile-carousel"],
        facultyProfiles.length > 1 && styles.compact,
        facultyProfiles.length === 3 && styles.two,
        facultyProfiles.length > 3 && styles.three
      )}
    >
      <div className={clsx(styles.wrapper, "wrapper")}>
        {facultyProfiles.length === 1 ? (
          <FacultyProfileSolo
            // @ts-expect-error type mismatch
            content={{
              ...facultyProfiles[0]?.fields,
              profilePicture:
                // @ts-expect-error type mismatch
                facultyProfiles[0]?.fields?.profilePicture?.fields?.file?.url,
              profilePictureDescription:
                // @ts-expect-error type mismatch
                facultyProfiles[0]?.fields?.profilePicture?.fields?.description,
            }}
            lightbox={false}
          />
        ) : (
          <>
            {facultyProfiles.length > 4 ||
            (isMobile && facultyProfiles.length > 1) ? (
              <SwiperCarousel {...swiperParams}>
                {facultyProfiles.map((item, index) => {
                  // @ts-expect-error type mismatch
                  const { name } = item?.fields || {};
                  const slideIndex = index + 1;
                  const activeSlide = (swiper?.activeIndex || 0) + 1;
                  const activeSlidesGap = activeSlide + slidesPerView;
                  const isVisible =
                    slideIndex >= activeSlide && slideIndex < activeSlidesGap;

                  return (
                    <SwiperSlide
                      key={`${name}-${index}`}
                      className={styles["swiper-slide"]}
                      // @ts-expect-error no in the typings
                      inert={isVisible ? undefined : "true"}
                      aria-hidden={!isVisible}
                    >
                      <FacultyProfileMulti
                        // @ts-expect-error type mismatch
                        content={item?.fields}
                        type={item?.component}
                      />
                    </SwiperSlide>
                  );
                })}
                {facultyProfiles.length === 3 && isDesktop && <div />}
              </SwiperCarousel>
            ) : (
              <Grid row>
                {facultyProfiles.map((item, index) => (
                  <Grid
                    column
                    sm={12}
                    md={6}
                    lg={
                      facultyProfiles.length === 3
                        ? 3
                        : 12 / facultyProfiles.length
                    }
                    key={`item-${index}`}
                  >
                    <FacultyProfileMulti
                      content={
                        item?.fields as unknown as FacultyProfileMultiProps
                      }
                      type={item?.component}
                    />
                  </Grid>
                ))}
                {facultyProfiles.length === 3 && isDesktop && (
                  <Grid column sm={12} md={6} lg={3}></Grid>
                )}
              </Grid>
            )}
          </>
        )}

        {facultyProfiles.length > 4 && (
          <button
            className={styles.pagination}
            tabIndex={0}
            onClick={goToNextSlide}
            aria-label={`Next slide, ${nextPage} of ${pagesCount}`}
          >
            <span className={styles.slide}>
              {String(currentPage).padStart(2, "0")}
            </span>
            <strong>/</strong>
            <span className="slides">
              {String(pagesCount).padStart(2, "0")}
            </span>
            <i className={styles["icon-arrow-long"]}>
              <IconArrowLong />
            </i>
          </button>
        )}
      </div>
    </div>
  );
};

export default FacultyProfileCarousel;
